import { LitElement, html, TemplateResult } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { FontSizes, FontWeights } from '../../../types/common/types';
import { EventTypes, SelectedListGeneratorStrings, SelectedListItem } from '../types';
import { GzEnumListWebComponent } from '../../gzEnumList';
import { GzSelectedListWebComponent } from '../../gzSelectedList';

@customElement('gz-selected-list-item-generator')
export class GzSelectedListItemGeneratorWebComponent extends LitElement {
  /**
   * @property
   * Property to store the filter options
   */
  @property({ type: Array, attribute: true, reflect: true })
  filterOptions: SelectedListItem[];

  /**
   * @property
   * Property to store the text translations
   */
  @property({ type: Object, attribute: false, reflect: false })
  strings: SelectedListGeneratorStrings = {
    sectionLeftTitle: 'Lorem ipsum left [***]',
    sectionRightTitle: 'Lorem ipsum right [***]',
    searchPlaceholderString: 'Search [***]',
    noSearchResultsString: 'No results found [***]',
    allLabel: 'All [***]',
    emptyStateText: 'No items selected yet[***]',
  };

  /**
   * @property
   * Property that allows to select all items from enum-list
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  canSelectAll = false;

  @query('#item-enum-list')
  private itemEnumListWebComponent: GzEnumListWebComponent;

  @query('gz-selected-list')
  private selectedListWebComponent: GzSelectedListWebComponent;

  static get styles() {
    return styles;
  }

  firstUpdated(): void {
    this.setSelectedItems();
  }

  render(): TemplateResult {
    return html`
      <div class="d-flex flex-row">
        <div class="d-flex flex-column section">
          <gz-text
            fontSize="${FontSizes.PARAGRAPH_11}"
            class="section-title text-uppercase mb-1"
            fontWeight="${FontWeights.MEDIUM}"
          >
            ${this.strings.sectionLeftTitle}
          </gz-text>
          <gz-enum-list
            id="item-enum-list"
            .filterOptions="${this.filterOptions}"
            .searchPlaceholderString="${this.strings.searchPlaceholderString}"
            .noSearchResultsString="${this.strings.noSearchResultsString}"
            .allItemsLabel="${this.strings.allLabel}"
            @optionChanged="${this.handleChange}"
            ?canSelectAll="${this.canSelectAll}"
          ></gz-enum-list>
        </div>
        <div class="d-flex flex-column section pl-4">
          <gz-text
            fontSize="${FontSizes.PARAGRAPH_11}"
            class="section-title text-uppercase mb-1"
            fontWeight="${FontWeights.MEDIUM}"
          >
            ${this.strings.sectionRightTitle}
          </gz-text>
          <gz-selected-list
            emptyStateText="${this.strings.emptyStateText}"
            @deleteItem="${this.handleDelete}"
          ></gz-selected-list>
        </div>
      </div>
    `;
  }

  private handleChange(): void {
    if (!this.itemEnumListWebComponent) {
      return;
    }
    this.setSelectedItems();
  }

  private setSelectedItems(): void {
    this.selectedListWebComponent.selectedItems = this.itemEnumListWebComponent.getCheckedOptions();
    this.dispatchEvent(
      new CustomEvent(EventTypes.CHANGE_SELECTED, {
        detail: { selectedItems: this.selectedListWebComponent.selectedItems },
      })
    );
  }

  private handleDelete(event: CustomEvent): void {
    this.filterOptions = this.itemEnumListWebComponent.filterOptions.map((item: SelectedListItem) =>
      item.value === event.detail.value
        ? {
            ...item,
            selected: false,
          }
        : item
    );

    this.itemEnumListWebComponent.filterOptions = this.itemEnumListWebComponent.filterOptions.filter(
      (item: SelectedListItem) => item.value !== event.detail.value
    );

    this.dispatchEvent(
      new CustomEvent(EventTypes.CHANGE_SELECTED, {
        detail: { selectedItems: this.selectedListWebComponent.selectedItems },
      })
    );
  }

  public getSelectedValues(): (string | number)[] {
    return this.itemEnumListWebComponent.getCheckedOptions().map(item => item.value);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-selected-list-item-generator': GzSelectedListItemGeneratorWebComponent;
  }
}
